import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

/** Shared types + small helpers for the Leads module. One copy, so the screens cannot drift. */

export interface LeadStatus {
  id: number; name: string; colour?: string | null; sortOrder: number;
  isWon: boolean; isLost: boolean; isDefault: boolean; isActive: boolean;
}
export interface LeadSource { id: number; name: string; isActive: boolean; sortOrder: number }
export interface LeadTag { id: number; name: string; colour?: string | null; isActive: boolean }
export interface LeadCustomField {
  id: number; key: string; label: string;
  fieldType: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'CHECKBOX';
  options?: string[] | null; isRequired: boolean; showInList: boolean; sortOrder: number; isActive: boolean;
}

export interface Lead {
  id: number;
  leadNo: string;
  companyName: string;
  brandName?: string | null;
  contactName?: string | null;
  designation?: string | null;
  mobile?: string | null;
  altMobile?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  gstin?: string | null;
  pan?: string | null;
  serviceType?: string | null;
  monthlyOrders?: number | null;
  avgWeightKg?: number | null;
  avgOrderValue?: number | null;
  productCategory?: string | null;
  currentCourier?: string | null;
  paymentMode?: string | null;
  codPercent?: number | null;
  salesChannel?: string | null;
  pickupCity?: string | null;
  pickupPincode?: string | null;
  pickupLocations?: number | null;
  deliveryZones?: string | null;
  expectedRate?: number | null;
  hasWarehouse?: boolean;
  wantsWms?: boolean;
  statusId?: number | null;
  status?: { id: number; name: string; colour?: string | null; isWon?: boolean; isLost?: boolean } | null;
  sourceId?: number | null;
  source?: { id: number; name: string } | null;
  ownerId?: number | null;
  ownerName?: string | null;
  rating?: string | null;
  estimatedValue?: number | null;
  expectedCloseDate?: string | null;
  nextFollowupAt?: string | null;
  lastContactedAt?: string | null;
  lostReason?: string | null;
  customFields?: Record<string, any> | null;
  notes?: string | null;
  convertedMerchantId?: number | null;
  deletedAt?: string | null;
  deletedByName?: string | null;
  deleteReason?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: LeadTag[];
  followups?: LeadFollowup[];
  activities?: LeadActivity[];
  _count?: { followups: number; activities: number };
}

export interface LeadFollowup {
  id: number;
  leadId: number;
  dueAt: string;
  type: string;
  notes?: string | null;
  status: 'PENDING' | 'DONE' | 'MISSED' | 'CANCELLED';
  outcome?: string | null;
  doneAt?: string | null;
  assignedToId?: number | null;
  assignedToName?: string | null;
  overdue?: boolean;
  lead?: Pick<Lead, 'id' | 'leadNo' | 'companyName' | 'contactName' | 'mobile' | 'email' | 'ownerId'> & {
    status?: { id: number; name: string; colour?: string | null } | null;
  };
}

export interface LeadActivity {
  id: number; leadId: number; type: string; message: string;
  userId?: number | null; userName?: string | null; createdAt: string;
}

export const RATINGS = ['HOT', 'WARM', 'COLD'];
export const SERVICE_TYPES = ['B2C', 'B2B', 'BOTH'];
export const PAYMENT_MODES = ['COD', 'PREPAID', 'BOTH'];
export const FOLLOWUP_TYPES = ['CALL', 'WHATSAPP', 'EMAIL', 'MEETING', 'VISIT'];

export const money = (n?: number | null) =>
  n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export const dateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export const dateOnly = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';

/** Tailwind classes for a rating chip — HOT reads hot, and the eye finds it without reading. */
export const ratingClass = (r?: string | null) =>
  r === 'HOT' ? 'bg-rose-50 text-rose-700 border-rose-200'
    : r === 'WARM' ? 'bg-amber-50 text-amber-700 border-amber-200'
      : r === 'COLD' ? 'bg-sky-50 text-sky-700 border-sky-200'
        : 'bg-slate-50 text-slate-500 border-slate-200';

/**
 * A wa.me link for an Indian mobile.
 *
 * WhatsApp needs the country code and nothing else — no +, spaces or dashes — and a number typed
 * as "+91 98765 43210" or "098765 43210" is the normal case in this data, so it is normalised here
 * rather than trusting whatever was pasted into the field.
 */
export function whatsappLink(mobile?: string | null, text?: string): string | null {
  const digits = String(mobile ?? '').replace(/\D/g, '');
  if (!digits) return null;
  const ten = digits.length > 10 ? digits.slice(-10) : digits;
  if (ten.length !== 10) return null;
  const base = `https://wa.me/91${ten}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

/** Masters (statuses / sources / tags / custom columns) — cached, every Leads screen uses them. */
export function useLeadMasters() {
  return useQuery({
    queryKey: ['lead-masters'],
    queryFn: async () => (await api.get<{
      statuses: LeadStatus[]; sources: LeadSource[]; tags: LeadTag[]; fields: LeadCustomField[];
    }>('/leads/masters')).data,
    staleTime: 5 * 60_000,
  });
}

/** Internal users a lead or a follow-up can be assigned to. */
export function useLeadUsers() {
  return useQuery({
    queryKey: ['lead-users'],
    queryFn: async () => (await api.get<{ id: number; name: string }[]>('/leads/assignable-users')).data,
    staleTime: 5 * 60_000,
  });
}
